/**
 * 单个会话的客户端状态机：历史分页、增量补拉、发送回执与重试、已读、上下文。
 *
 * 约束来源：
 * - CONTRACT.md 第 27 行：before/after 互斥；首屏最新一页升序，after 用于增量升序；
 *   游标为规范化十进制字符串，按数值比较；页有界，不做无限补空。
 * - 第 28 行：clientMessageId 对同一发送者+会话唯一且绑定载荷哈希；
 *   相同 key 重复提交返回同一条 Message（幂等），不同载荷 1901005。
 * - 第 29 行：已读游标单调不回退。
 * - 需求 6 / 9.3：明确的"发送中 / 服务端已接收 / 失败"，重试复用同一幂等键与同一快照；
 *   内部备注不得泄露给客户。
 */

import { isCanonicalDecimal, newRequestId } from '@support/platform';

import { SupportError, isSupportError } from './errors';
import { MessageStore } from './messages';
import type {
  ConversationTransport,
  Message,
  MessageContentType,
  MessageVisibility,
  PageContext,
  PendingSend,
  SendMessageBody,
  SendStatus,
  SupportAttachment,
  SupportRole,
} from './types';

export interface ConversationSessionOptions {
  conversationId: string;
  /** 客户端自己的角色，用于本地拦截越权操作（服务端仍会强制）。 */
  role: SupportRole;
  transport: ConversationTransport;
  /** 单页条数上限，默认 50（与协议一致）。 */
  pageSize?: number;
}

export interface SendOptions {
  attachmentIds?: string[];
  clientMessageId?: string;
  contentType?: MessageContentType;
  context?: PageContext;
  text: string;
  visibility?: MessageVisibility;
}

export interface PendingSendHandle {
  clientMessageId: string;
  /** 服务端回执落地后 resolve 为该条 Message；失败则 reject SupportError。 */
  settled: Promise<Message>;
  /** 实时状态：sending / accepted / failed（只读快照，随状态机变化）。 */
  readonly status: SendStatus;
}

interface SubmittedPayload extends SendMessageBody {}

const DEFAULT_PAGE_SIZE = 50;

export class ConversationSession {
  readonly conversationId: string;

  readonly messages = new MessageStore();

  readonly role: SupportRole;

  private readonly pageSize: number;

  private readonly pending = new Map<string, PendingSend>();

  private readonly submitted = new Map<string, SubmittedPayload>();

  private readonly transport: ConversationTransport;

  private hasMoreBeforeValue = false;

  private hasMoreAfterValue = true;

  private readCursor: null | string = null;

  constructor(options: ConversationSessionOptions) {
    this.conversationId = options.conversationId;
    this.role = options.role;
    this.transport = options.transport;
    this.pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  }

  get hasMoreBefore(): boolean {
    return this.hasMoreBeforeValue;
  }

  get hasMoreAfter(): boolean {
    return this.hasMoreAfterValue;
  }

  get currentReadCursor(): null | string {
    return this.readCursor;
  }

  /**
   * 首屏：取最新一页（服务端按升序返回），并记录是否还有更早历史。
   */
  async loadInitial(): Promise<Message[]> {
    const page = await this.transport.listMessages(this.conversationId, {
      limit: this.pageSize,
    });
    const { added } = this.messages.merge(page.items);
    this.hasMoreBeforeValue = page.hasMore;
    this.hasMoreAfterValue = true;
    return added;
  }

  /** 向上翻页：before=当前最旧序号（与 after 互斥）。 */
  async loadOlder(): Promise<Message[]> {
    if (!this.hasMoreBeforeValue) return [];
    const before = this.messages.oldestSequence();
    if (before === null) return [];
    const page = await this.transport.listMessages(this.conversationId, {
      before,
      limit: this.pageSize,
    });
    const { added } = this.messages.merge(page.items);
    this.hasMoreBeforeValue = page.hasMore;
    return added;
  }

  /**
   * 增量补拉：after=当前可见高水位。
   * 单次调用最多补 {@link maxSyncPages} 页，且空页立即停止，
   * 避免"因内部备注不可见而无限补拉"。
   */
  async loadNewer(maxSyncPages = 3): Promise<Message[]> {
    const collected: Message[] = [];
    const visibility: MessageVisibility = this.role === 'customer' ? 'public' : 'internal';
    for (let i = 0; i < maxSyncPages; i += 1) {
      const after = this.messages.cursorAfter(visibility);
      if (after === null) break;
      const page = await this.transport.listMessages(this.conversationId, {
        after,
        limit: this.pageSize,
      });
      if (page.items.length === 0) break;
      const { added } = this.messages.merge(page.items);
      collected.push(...added);
      if (!page.hasMore) break;
    }
    return collected;
  }

  /** 可见消息（客户视角自动排除内部备注）。 */
  visibleMessages(): Message[] {
    return this.role === 'customer' ? this.messages.visibleMessages() : this.messages.list();
  }

  pendingSends(): PendingSend[] {
    return [...this.pending.values()].filter((item) => item.status !== 'accepted');
  }

  /**
   * 发送消息。
   * 立即返回一条"sending"待发记录；服务端回执后落为 accepted，失败落为 failed。
   * 失败记录保留原 clientMessageId 与原始载荷（含上下文快照对象），供 {@link retry} 复用。
   */
  send(options: SendOptions): PendingSendHandle {
    const visibility = options.visibility ?? 'public';
    if (this.role === 'customer' && visibility === 'internal') {
      throw new Error('客户不具备创建内部备注的权限');
    }
    const text = options.text.trim();
    if (text === '' && (options.attachmentIds?.length ?? 0) === 0) {
      throw new Error('空消息不发送');
    }
    const clientMessageId = options.clientMessageId ?? newRequestId();
    const contentType =
      options.contentType ?? inferContentType(options.attachmentIds?.length ?? 0);
    const payload: SendMessageBody = {
      clientMessageId,
      contentType,
      text,
      visibility,
      ...(options.attachmentIds?.length ? { attachmentIds: options.attachmentIds } : {}),
      // 上下文快照按引用保存：重试沿用同一对象，绝不重新采集
      ...(options.context ? { context: options.context } : {}),
    };
    this.submitted.set(clientMessageId, payload);

    const record: PendingSend = {
      clientMessageId,
      contentType,
      status: 'sending',
      text,
      visibility,
    };
    this.pending.set(clientMessageId, record);

    const settled = this.dispatch(clientMessageId, payload, record);
    return this.handle(clientMessageId, record, settled);
  }

  /** 原 key 重试：复用完全相同的幂等键、文本、附件与上下文快照。 */
  retry(clientMessageId: string): null | PendingSendHandle {
    const payload = this.submitted.get(clientMessageId);
    const record = this.pending.get(clientMessageId);
    if (!payload || !record) return null;
    if (record.status === 'sending') {
      // 已在发送中，避免并发提交同一幂等键
      return null;
    }
    record.status = 'sending';
    delete record.errorKind;
    delete record.errorMessage;
    const settled = this.dispatch(clientMessageId, payload, record);
    return this.handle(clientMessageId, record, settled);
  }

  /**
   * 构造待发句柄。
   *
   * status 必须是**实时只读 getter**，而不是构造时拍下的快照：
   * 调用方（工作台气泡、客户悬浮窗）在 await settled 之前/之后读到的
   * 一定是最新状态；同时它没有 setter，外部无法伪造"已接收"。
   */
  private handle(
    clientMessageId: string,
    record: PendingSend,
    settled: Promise<Message>,
  ): PendingSendHandle {
    return {
      clientMessageId,
      settled,
      get status(): SendStatus {
        return record.status;
      },
    };
  }

  /** 放弃一条失败消息（例如用户删除）。 */
  discard(clientMessageId: string): void {
    const record = this.pending.get(clientMessageId);
    if (record && record.status === 'sending') return;
    this.pending.delete(clientMessageId);
    this.submitted.delete(clientMessageId);
  }

  /** 已读游标：单调不回退，且以服务端返回值为准。 */
  async markRead(cursor?: string): Promise<null | string> {
    const target = cursor ?? this.messages.cursorAfter('public');
    if (target === null) return this.readCursor;
    if (this.readCursor !== null && compareCursor(target, this.readCursor) <= 0) {
      return this.readCursor;
    }
    const result = await this.transport.markRead(this.conversationId, target);
    if (this.readCursor === null || compareCursor(result.cursor, this.readCursor) > 0) {
      this.readCursor = result.cursor;
    }
    return this.readCursor;
  }

  async pushContext(context: PageContext): Promise<boolean> {
    const result = await this.transport.pushContext(this.conversationId, context);
    return result.accepted;
  }

  async loadContexts(): Promise<PageContext[]> {
    const result = await this.transport.listContexts(this.conversationId);
    return result.items;
  }

  async uploadAttachment(
    file: Blob,
    visibility: MessageVisibility,
    fileName?: string,
  ): Promise<SupportAttachment> {
    if (this.role === 'customer' && visibility === 'internal') {
      throw new Error('客户不具备上传内部附件的权限');
    }
    return await this.transport.uploadAttachment(
      this.conversationId,
      file,
      visibility,
      fileName,
    );
  }

  private async dispatch(
    clientMessageId: string,
    payload: SubmittedPayload,
    record: PendingSend,
  ): Promise<Message> {
    try {
      const message = await this.transport.sendMessage(this.conversationId, payload);
      this.messages.merge([message]);
      // 先落"已接收"再移除待发记录：句柄上的实时 getter 在 await 之后读到 accepted
      record.status = 'accepted';
      this.pending.delete(clientMessageId);
      this.submitted.delete(clientMessageId);
      return message;
    } catch (error) {
      record.status = 'failed';
      const supportError = toSupportError(error);
      record.errorKind = supportError.kind;
      record.errorMessage = supportError.message;
      throw supportError;
    }
  }
}

function inferContentType(attachmentCount: number): MessageContentType {
  return attachmentCount > 0 ? 'file' : 'text';
}

function compareCursor(left: string, right: string): number {
  if (!isCanonicalDecimal(left) || !isCanonicalDecimal(right)) return 0;
  const a = BigInt(left);
  const b = BigInt(right);
  if (a === b) return 0;
  return a > b ? 1 : -1;
}

/** 把任意异常收敛为 SupportError，便于 UI 按 kind 展示与决策。 */
export function toSupportError(error: unknown): SupportError {
  if (isSupportError(error)) return error;
  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>;
    if (typeof record.code === 'number') {
      return new SupportError({
        code: record.code,
        message: typeof record.message === 'string' ? record.message : '请求失败',
      });
    }
  }
  return new SupportError({
    code: 0,
    kind: 'network',
    message: error instanceof Error ? error.message : String(error),
  });
}